// A default-engineer picker card (Session ARA, S-4), shared by the Site and Contract detail
// pages. BUILT TO D84's STANDING RECOMMENDATION (docs/ELE-V2-DIRECTOR-DECISIONS-2026-07-19.md,
// option (a)), NOT A CONFIRMED DIRECTOR RULING -- D84 itself is still open. Follows the
// SiteDetailPage AccessDetailsPanel "toggle to inline-edit" idiom: a plain value display with a
// "Change" link that swaps in a select + Save/Cancel, rather than a full modal, since this is a
// single field.
//
// Deliberately CONTROLLED (a `value` prop, never a self-fetch of the entity itself): the Site page
// already holds `site.defaultEngineerId` in its own state, and the Contract page cannot (its
// general detail DTO is out of reach of this session's scope -- see the header of
// contractDefaultEngineer.routes.ts), so each caller decides how it sources and persists `value`.
// This component owns only the picker UI and the engineer list.
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./Button";
import { Loader } from "./Loader";

export interface DefaultEngineerCardProps {
  // The currently-set defaultEngineerId, or null. `undefined` renders a loading state (the
  // Contract page uses this while its own small GET is in flight).
  value: string | null | undefined;
  // Persists the choice (null clears it) and resolves once the write has landed; the caller is
  // responsible for updating whatever holds `value`. Rejecting shows the generic error message.
  onSave: (defaultEngineerId: string | null) => Promise<void>;
  description: string;
  // Hides the "Change" control for a caller with no write access to the underlying route; the
  // value still displays. Defaults to true (both detail pages this ships on are office/admin only).
  canEdit?: boolean;
}

export function DefaultEngineerCard({ value, onSave, description, canEdit = true }: DefaultEngineerCardProps) {
  const [engineers, setEngineers] = useState<{ id: string; name: string }[]>([]);
  const [engineersLoaded, setEngineersLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    // The active-engineer register rows (Job/JobSheet.assignedTo targets), NOT the admin-only
    // /api/engineers list -- that one is keyed by User id, a different id entirely, and would
    // silently write the wrong foreign key. See src/lib/api.ts's own comment on scheduling.engineers.
    //
    // Wrapped in try/catch (not just a rejection handler): a test fixture mocking `api` without a
    // `scheduling` key throws SYNCHRONOUSLY on the property read below, before any promise exists
    // to reject, which would otherwise crash the whole page render rather than just leaving this
    // one card's picker list empty.
    (async () => {
      try {
        const rows = await api.scheduling.engineers();
        if (alive) setEngineers(Array.isArray(rows) ? rows : []);
      } catch {
        // Best-effort: the card still shows the current value by id even with no name list yet.
      } finally {
        if (alive) setEngineersLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  function startEdit() {
    setSelected(value ?? "");
    setError(null);
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await onSave(selected.length > 0 ? selected : null);
      setEditing(false);
    } catch {
      setError("Could not save the default engineer. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const currentName =
    value == null
      ? "Not set"
      : (engineers.find((e) => e.id === value)?.name ??
        (engineersLoaded ? "An engineer no longer active" : <Loader shape="inline" />));

  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg text-ele-blue">Default engineer</h2>
          <p className="mt-1 text-xs text-ele-body">{description}</p>
        </div>
        {!editing && canEdit && (
          <button
            type="button"
            onClick={startEdit}
            className="shrink-0 font-menu text-xs tracking-wide text-ele-green underline-offset-2 hover:underline"
          >
            Change
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-3">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green sm:w-72"
          >
            <option value="">Not set</option>
            {engineers.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          {error && (
            <p role="alert" className="mt-2 text-xs text-ele-danger">
              {error}
            </p>
          )}
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={() => void save()} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 text-sm text-ele-strong">{value === undefined ? <Loader shape="inline" /> : currentName}</div>
      )}
    </section>
  );
}
